import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchExportAlerts } from "../../services/apis";
import { useDispatch } from "react-redux";
import { Box, Typography } from "@mui/material";

import { setBreadcrumbText, setHeaderText } from "../../redux/Slice/Navigation/NavigationSlice";
import AlertsTable from "../../features/Alerts/AlertsTable";
import { InfoFooter } from "../../features";

export interface AlertsRequestData {
  search_substring?: string;
  pageNumber?: string;
  pageSize?: string;
  sortOrder?: string;
  sortField?: string;
  project_id?: string;
  site_id?: string;
  unit_id?: string;
  active_only?: boolean;
}

export default function Alerts() {
  const dispatch = useDispatch();
  const [enableSearchQuery, setEnableSearchQuery] = useState(false);
  const [alertsRequestData, setAlertsRequestData] = useState<AlertsRequestData>({
    search_substring: "",
    pageNumber: "0",
    pageSize: "10",
    sortOrder: "asc",
    sortField: "alert_dismissed",
    project_id: "",
    site_id: "",
    unit_id: "",
    active_only: true,
  });

  // Set breadcrumb and header text only once on mount
  useEffect(() => {
    dispatch(setBreadcrumbText([""]));
    dispatch(setHeaderText("Alerts"));
  }, [dispatch]);

  const {
    data: exportAlertsData,
    isLoading: isLoadingAlerts,
    isSuccess: isSuccessAlerts,
  } = useQuery({
    queryKey: ["exportAlertsData", alertsRequestData],
    queryFn: fetchExportAlerts,
    enabled: enableSearchQuery,
  });

  useEffect(() => {
    setEnableSearchQuery(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertsRequestData]);

  useEffect(() => {
    if (!isLoadingAlerts && exportAlertsData !== undefined && isSuccessAlerts) {
      setEnableSearchQuery(false);
    }
  }, [exportAlertsData, isLoadingAlerts, isSuccessAlerts]);

  // console.log("Fetched export alerts data:", exportAlertsData);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "calc(100vh - 64px)", // Adjust to account for the footer height
      }}
    >
      <Box sx={{ flex: 1, padding: "24px" }}>
        <Typography variant="h2" sx={{ marginTop: "20px", marginBottom: "10px" }}>
          Active Alerts ({exportAlertsData?.active_count || 0})
        </Typography>
        <AlertsTable
          exportAlertsData={exportAlertsData}
          isLoadingAlerts={isLoadingAlerts}
          isSuccessAlerts={isSuccessAlerts}
          alertsRequestData={alertsRequestData}
          setAlertsRequestData={setAlertsRequestData}
          setEnableSearchQuery={setEnableSearchQuery}
        />
      </Box>
      <Box sx={{ mt: "auto", width: "100%" }}>
        <InfoFooter />
      </Box>
    </Box>
  );
}
